"use client"

import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import type { ClawHubSkill, SkillCategoryItem } from "@/lib/clawhub-api"
import { SkillDetailModal } from "./skill-detail-modal"
import { LoginRequiredModal } from "@/src/components/auth/login-required-modal"
import { SafeClawLogo } from "@/src/components/ui/safeclaw-logo"
import { ArrowLeft } from "lucide-react"
import { useAppContext } from "@/lib/app-context"
import { SkillsSearchBar } from "./skills-search-bar"
import { SkillsHeroSection } from "./skills-hero-section"
import { SkillsCategoryFilter } from "./skills-category-filter"
import { SkillsGrid } from "./skills-grid"
import { useSkillsFilter } from "@/lib/hooks/use-skills-filter"
import { analytics, trackPageDuration } from "@/src/utils/analytics"
import "./skills-landing-page.less"
import "./skills-search-bar.less"

export function SkillsLandingPage({
  onGoToChat,
  onGoToLogin,
  onGoToLanding,
  isLoggedIn = false,
  scenarioId,
}: {
  onGoToChat?: () => void
  onGoToLogin?: () => void
  /** 点击 Logo 返回对话落地页（与 AppShell 一致） */
  onGoToLanding?: () => void
  isLoggedIn?: boolean
  /** Current demo scenario ID — shows scenario skills panel */
  scenarioId?: string
}) {
  const { setWaveIntensity } = useAppContext()
  const filter = useSkillsFilter()
  const [selectedSkill, setSelectedSkill] = useState<ClawHubSkill | null>(null)
  const [showLoginRequired, setShowLoginRequired] = useState(false)

  useEffect(() => trackPageDuration("skills"), [])

  // 顶部 tag pill bar 数据：popularTags 前置"全部"项
  const tagFilterItems = useMemo<SkillCategoryItem[]>(() => [
    { id: 0, value: "all", label: "全部", count: 0, createdAt: "", updatedAt: "" },
    ...filter.popularTags,
  ], [filter.popularTags])

  const rootRef = useRef<HTMLDivElement>(null!)
  const heroTopRef = useRef<HTMLDivElement>(null!)
  const prevScrollTopRef = useRef(0)
  const resultsRef = useRef<HTMLDivElement>(null!)

  // Wrap filter handlers to include wave intensity changes
  const handleSearch = useCallback(() => {
    filter.handleSearch()
    if (filter.query.trim() || filter.selectedTag || filter.selectedCategory !== "all") {
      setWaveIntensity(0.15)
    }
    // 埋点：搜索行为
    if (filter.query.trim()) {
      analytics.track('SkillSearch', {
        keyword: filter.query.trim(),
        category: filter.selectedCategory,
        tag: filter.selectedTag ?? '',
      })
    }
  }, [filter, setWaveIntensity])

  const handleTagClick = useCallback((tagName: string) => {
    filter.handleTagClick(tagName)
    setWaveIntensity(0.15)
  }, [filter, setWaveIntensity])

  const handleCategoryClick = useCallback((catId: string) => {
    filter.handleCategoryClick(catId)
    setWaveIntensity(0.15)
  }, [filter, setWaveIntensity])

  const handleClear = useCallback(() => {
    filter.handleClear()
    setWaveIntensity(1)
  }, [filter, setWaveIntensity])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch()
    }
  }, [handleSearch])

  // Reset scroll when page becomes visible (SPA mode switch)
  useEffect(() => {
    const el = rootRef.current
    const parent = el?.parentElement
    if (!el || !parent) return
    let wasActive = getComputedStyle(parent).pointerEvents !== "none"
    const mo = new MutationObserver(() => {
      const isActive = getComputedStyle(parent).pointerEvents !== "none"
      if (isActive && !wasActive) {
        filter.mainScrollRef.current?.scrollTo({ top: 0 })
        if (heroTopRef.current) heroTopRef.current.style.transform = ""
        prevScrollTopRef.current = 0
        setWaveIntensity(1)
      }
      if (!isActive && wasActive) {
        setWaveIntensity(1)
      }
      wasActive = isActive
    })
    mo.observe(parent, { attributes: true, attributeFilter: ["class"] })
    return () => mo.disconnect()
  }, [])

  // Scroll-driven parallax + wave intensity
  useEffect(() => {
    const scrollEl = filter.mainScrollRef.current
    if (!scrollEl) return
    let rafId: number
    const onScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const scrollTop = scrollEl.scrollTop
        prevScrollTopRef.current = scrollTop

        // Wave intensity: fade background as user scrolls past hero
        const viewH = window.innerHeight
        const fadeStart = viewH * 0.25
        const fadeEnd = viewH * 0.75
        if (scrollTop <= fadeStart) {
          setWaveIntensity(1)
        } else if (scrollTop >= fadeEnd) {
          setWaveIntensity(0.15)
        } else {
          setWaveIntensity(1 - 0.85 * ((scrollTop - fadeStart) / (fadeEnd - fadeStart)))
        }

        // Parallax: hero title area scrolls ~40% faster to avoid overlap
        const heroEl = heroTopRef.current
        if (heroEl) {
          heroEl.style.transform = `translateY(-${Math.min(scrollTop * 0.4, 200)}px)`
        }
      })
    }
    scrollEl.addEventListener("scroll", onScroll, { passive: true })
    onScroll()
    return () => {
      cancelAnimationFrame(rafId)
      scrollEl.removeEventListener("scroll", onScroll)
    }
  }, [setWaveIntensity])

  return (
    <div ref={rootRef} className="flex h-dvh flex-col relative pt-14">
      {/* Logo — 固定左上角 */}
      <button
        type="button"
        onClick={onGoToLanding}
        className="skills-logo-btn skills-logo-btn--visible"
        aria-label="返回首页"
      >
        <SafeClawLogo
          height={26}
          className="opacity-50 hover:opacity-90 transition-opacity"
        />
      </button>

      {/* 返回按钮 — absolute 浮于搜索框上方，hasSearched 时才渲染 */}
      {filter.hasSearched && (
        <div className="skills-back-float">
          {/* 桌面端：跳过侧边栏宽度与右侧内容区左对齐 */}
          <div className="hidden md:block pl-[calc(10rem+1.25rem)]">
            <button type="button" onClick={handleClear} className="skills-back-btn">
              <ArrowLeft className="h-4 w-4" />
              返回
            </button>
          </div>
          {/* 移动端：靠左 */}
          <div className="md:hidden">
            <button type="button" onClick={handleClear} className="skills-back-btn">
              <ArrowLeft className="h-4 w-4" />
              返回
            </button>
          </div>
        </div>
      )}

      {/* Main content area */}
      <div
        ref={filter.mainScrollRef}
        className={`flex-1 flex flex-col relative ${filter.hasSearched ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden no-scrollbar"
          }`}
      >
        <SkillsHeroSection
          hasSearched={filter.hasSearched}
          popularTags={filter.popularTags}
          onTagClick={handleTagClick}
          heroTopRef={heroTopRef}
          searchBar={
            <SkillsSearchBar
              ref={filter.inputRef}
              query={filter.query}
              onQueryChange={filter.handleQueryChange}
              onSearch={handleSearch}
              onClear={handleClear}
              onKeyDown={handleKeyDown}
              hasSearched={filter.hasSearched}
              isDocked={false}
            />
          }
          recommendedSkills={filter.recommendedSkills}
          recSentinelRef={filter.recSentinelRef}
          recLoadingMore={filter.recLoadingMore}
          recHasMore={filter.recHasMore}
          onLoadMoreRecommended={filter.loadMoreRecommended}
          onSkillClick={setSelectedSkill}
          onSkillUpdate={(updated) => {
            filter.updateSkill(updated)
            setSelectedSkill((prev) => prev?.id === updated.id ? updated : prev)
          }}
        />

        {filter.hasSearched && (
          <>
            {/* ── Category pills (搜索后贴在英雄区下方、结果上方) ── */}
            <SkillsCategoryFilter
              tags={tagFilterItems}
              selectedTag={filter.selectedTag}
              onTagClick={(tagName) => {
                if (!tagName) {
                  // 点击"全部" → 清除 tag 过滤，保留当前关键词重新搜索
                  filter.clearTagFilter()
                  setWaveIntensity(0.15)
                } else {
                  handleTagClick(tagName)
                }
              }}
              onClearTag={filter.clearTagFilter}
            />

            {/* ── Results grid ── */}
            <div
              ref={resultsRef}
              className="flex-1 overflow-y-auto no-scrollbar px-4 pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500 min-h-0"
            >
              <SkillsGrid
                skills={filter.skills}
                loading={filter.loading}
                loadingMore={filter.loadingMore}
                total={filter.total}
                hasMore={filter.hasMore}
                sortBy={filter.sortBy}
                onSortChange={filter.handleSortChange}
                onLoadMore={filter.handleLoadMore}
                onSkillClick={setSelectedSkill}
                onTagClick={handleTagClick}
                categories={filter.categories}
                selectedCategory={filter.selectedCategory}
                onCategoryClick={handleCategoryClick}
                onBack={handleClear}
                onSkillUpdate={(updated) => {
                  filter.updateSkill(updated)
                  setSelectedSkill((prev) => prev?.id === updated.id ? updated : prev)
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Detail Modal */}
      {selectedSkill && (
        <SkillDetailModal
          skill={selectedSkill}
          onClose={() => setSelectedSkill(null)}
          isLoggedIn={isLoggedIn}
          onLoginRequired={() => {
            setSelectedSkill(null)
            setShowLoginRequired(true)
          }}
          onSkillChange={(updated) => {
            filter.updateSkill(updated)
            setSelectedSkill(updated)
          }}
        />
      )}

      {/* Login Required Modal */}
      {showLoginRequired && (
        <LoginRequiredModal
          onClose={() => setShowLoginRequired(false)}
          onGoToLogin={() => {
            setShowLoginRequired(false)
            onGoToLogin?.()
          }}
        />
      )}
    </div>
  )
}
